import { authenticationService } from '@core/services/authentication/AuthenticationService';
import { MfaPayload } from '@accountFederation/modules/securityCheck/types';
import { getAccountConfig } from '@accountFederation/config/accountConfig';
import { getFaceIdLogger } from '@accountFederation/modules/securityCheck/logging';
import {
    recordDurationSecondsEnd,
    recordDurationSecondsStep,
} from '@superbet-group/web.observability.sdk-hooks';

type MfaStatusCheckParams = {
    mfaPayload: MfaPayload;
    verifiedAction: () => void;
    failedAction: () => void;
};

export function useMfaStatusCheck(timeoutInSeconds = 60) {
    const { mfaCheckStatusMinTimeoutMs, mfaCheckStatusMaxTimeoutMs } = getAccountConfig();
    const { isMfaStatusCheckMitigationEnabled, isLoggingEnabled } = getAccountConfig().mfaDuplicateCallMitigationConfig;
    let lastOtpId = '';

    const checkMfaStatus = ({ mfaPayload, verifiedAction, failedAction }: MfaStatusCheckParams) => {
        const logger = getFaceIdLogger(mfaPayload?.flowName);
        const maybeLogMfaEvent = (label: string, shouldLog = true) => {
            if (!shouldLog) {
                return;
            }
            logger(label);
        };
        const otpId = mfaPayload?.otpId || mfaPayload?.otp_id; // TODO: Look into refactor across system to only one field and style

        maybeLogMfaEvent('mfa_status_check_start');

        if (otpId === lastOtpId && isMfaStatusCheckMitigationEnabled) {
            return;
        }

        maybeLogMfaEvent('mfa_status_check_duplicate_start', otpId === lastOtpId && isLoggingEnabled);

        lastOtpId = otpId;

        const statusActions = {
            verified: verifiedAction, // TODO: Verified should become early exit / duplicate call
            active: verifiedAction,
            failed: failedAction,
            expired: failedAction,
            no_process: failedAction,
        };

        const maxChecks = Math.ceil(timeoutInSeconds / (mfaCheckStatusMaxTimeoutMs / 1000));
        let currentCheck = 0;
        let checkStatusTimeoutMs = mfaCheckStatusMinTimeoutMs;
        const fallbackTimeoutMs = 100;

        const checkStatus = async () => {
            currentCheck += 1;

            try {
                recordDurationSecondsStep('player_verification', '6_check_mfa_status_request', {
                    attempt_number: currentCheck,
                    attempt_timeout_ms: checkStatusTimeoutMs,
                });

                const response = await authenticationService.getMfaStatus(mfaPayload);

                recordDurationSecondsStep('player_verification', '7_check_mfa_status_response', {
                    status: response.data.status,
                });

                const action = statusActions[response.data.status as keyof typeof statusActions];

                if (action) {
                    action();
                    maybeLogMfaEvent(`mfa_status_${response.data.status}`);
                    recordDurationSecondsEnd('player_verification', '8_verification_completed');
                } else if (currentCheck < maxChecks) {
                    maybeLogMfaEvent('mfa_status_check_tick', isLoggingEnabled);
                    checkStatusTimeoutMs = checkStatusTimeoutMs ? Math.min(checkStatusTimeoutMs * 2, mfaCheckStatusMaxTimeoutMs) : fallbackTimeoutMs; // 200, 400, 800, 1600, 3000, 3000..
                    setTimeout(checkStatus, checkStatusTimeoutMs);
                } else {
                    maybeLogMfaEvent('mfa_status_check_end');
                    failedAction();
                }
            } catch (error) {
                maybeLogMfaEvent('mfa_status_check_error');
                failedAction();
            }
        };

        setTimeout(checkStatus, checkStatusTimeoutMs);
    };

    return {
        checkMfaStatus,
    };
}
